import { useRouteError, type RouteObject } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { esErrorDeChunk } from '../lib/recargaPorDeploy';

/**
 * Lo que se ve cuando una ruta explota. Sin esto sale la pantalla cruda de React Router:
 * "Unexpected Application Error!" y el mensaje técnico del navegador.
 *
 * (28/08/2026) Es lo que vio Katherine Alanis —`'text/html' is not a valid JavaScript MIME
 * type`— y lo reportó como un bug de la búsqueda de clientes. `lazyConRecarga` ya recarga
 * sola en ese caso; esto es para cuando la recarga no alcanzó (se corta la conexión en el
 * medio, el chunk de verdad no está). Que el agente lea qué pasó y qué hacer, en castellano.
 */
export function PantallaDeError() {
  const error = useRouteError();
  const porDeploy = esErrorDeChunk(error);
  const detalle = error instanceof Error ? error.message : String(error ?? '');

  // Ocupa la pantalla entera: el errorElement va en las rutas de primer nivel, así que
  // reemplaza también el layout del CRM y no hay nada más alrededor. `dvh` es por el
  // iPhone, donde `vh` cuenta la barra del navegador y el contenido queda corrido.
  return (
    <Box sx={{ minHeight: ['100vh', '100dvh'], display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={2} alignItems="center" textAlign="center" maxWidth={460}>
        <Typography variant="h6">
          {porDeploy ? 'Salió una versión nueva del CRM' : 'No pudimos abrir esta pantalla'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {porDeploy
            ? 'Tu navegador todavía tiene guardada la versión anterior. Recargá la página y seguís donde estabas.'
            : 'Probá recargar la página. Si vuelve a pasar, avisanos desde el botón de soporte y lo miramos.'}
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
          Recargar
        </Button>
        {!porDeploy && detalle && (
          <Typography variant="caption" color="text.disabled" sx={{ wordBreak: 'break-word' }}>
            {detalle.slice(0, 200)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Le cuelga la pantalla a cada ruta de primer nivel. Va por acá y no ruta por ruta porque
 * el `errorElement` se hereda hacia abajo: una sola línea cubre las 145 páginas lazy.
 */
export function conPantallaDeError(rutas: RouteObject[]): RouteObject[] {
  return rutas.map((r) => (r.errorElement ? r : { ...r, errorElement: <PantallaDeError /> }));
}
